import { Link, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PreviewBanner } from "@/components/sci/PreviewBanner";
import { SciMetroLine } from "@/components/sci/SciMetroLine";
import { QSection } from "@/components/sci/subject/Bits";
import { SubjectHero, WhatIs } from "@/components/sci/subject/HeroWhat";
import { WhoFits, Study, Offered, Rankings } from "@/components/sci/subject/Middle";
import { Careers, Roi, WhereToStudy } from "@/components/sci/subject/Lower";
import { NotVerified } from "@/components/sci/Sections";
import { SciSubjectLite, SubjectSwitcher } from "@/components/sci/subject/Lite";
import { SciSubjectGuide } from "@/components/sci/subject/Guide";
import { useSciPreview, shortName } from "@/components/sci/useSciPreview";
import { SciNotFound as NotFound, SciLoadError } from "@/components/sci/SciNotFound";

export const metroCourses = (courses) => courses.map((c) => ({ degree_id: c.degree_id, label: shortName(c), sub: `${c.award || ""} ${c.title}`.trim() }));

const INTL = ["economics", "computer-science"];

export default function SciSubjectPreview() {
  const { slug } = useParams();
  const draft = useSearchParams()[0].get("draft") === "1";
  const { loading, data: d, missing, error, retry } = useSciPreview(`/sci/preview/subjects/${slug}${draft ? "?draft=1" : ""}`);
  if (loading) return <div className="min-h-[60vh]" data-testid="sci-subject-loading" />;
  if (missing) return <NotFound />;
  if (error) return <SciLoadError onRetry={retry} />;
  if (d.guide) return (
    <>
      <SciSubjectGuide d={d} />
      {INTL.includes(slug) && (
        <Link to={`/preview/sci/subjects/${slug}/international`} data-testid="sci-intl-link"
          className="fixed bottom-4 right-4 z-40 rounded-full bg-[#002147]/90 px-3 py-1.5 text-[11px] text-white shadow-lg transition-colors hover:bg-[#0b3566] md:bottom-5 md:left-5 md:right-auto md:px-4 md:py-2 md:text-xs">
          <span className="md:hidden">International</span><span className="hidden md:inline">International evidence (admin preview)</span>
        </Link>
      )}
    </>
  );
  if (d.lite) return <SciSubjectLite d={d} />;
  return (
    <div className="bg-[#FDFBF7]" data-testid="sci-subject-page">
      <Helmet><meta name="robots" content="noindex,nofollow" /><title>Studying Philosophy · private preview</title></Helmet>
      <PreviewBanner runId={d.run_ids.join(" + ")} />
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SubjectSwitcher current="philosophy" />
        <SubjectHero d={d} />
        <QSection id="what" n={1} title="What is Philosophy?" lede="In the words of the UK's subject benchmark and the universities that teach it."><WhatIs d={d} /></QSection>
        <QSection id="who" n={2} title="Who enjoys it, and will I?" lede="The interests, habits and strengths Philosophy rewards, then what your own profile says."><WhoFits d={d} /></QSection>
        <QSection id="study" n={3} title="What will I study?" lede="Topics shared across UK degrees, then how four real courses differ."><Study d={d} /></QSection>
        <QSection id="offered" n={4} title="How widely is it offered?"><Offered o={d.offer} /></QSection>
        <QSection id="rankings" n={5} title="Who's best at Philosophy?" lede="An honest answer: we don't publish a ranking until our own method is validated."><Rankings r={d.rankings} /></QSection>
        <QSection id="careers" n={6} title="Where does it lead?"><Careers d={d} /></QSection>
        <QSection id="roi" n={7} title="Is it worth it?" lede="Earnings for all Philosophy graduates, then at specific courses, with fees. Each figure says whose data it is."><Roi roi={d.roi} /></QSection>
        <QSection id="where" n={8} title="Where should I study it?" lede="Every UK Philosophy and PPE course in the official list. Signed in, they are ordered by your own course fit; otherwise A to Z while the WUA ranking is in development.">
          <div className="mb-8"><SciMetroLine subjectSlug="philosophy" courses={metroCourses(d.courses)} current="subject" /></div>
          <WhereToStudy d={d} />
        </QSection>
        <section className="border-t border-stone-200 py-10" data-testid="sci-section-hidden">
          <NotVerified testId="sci-subject-hidden-count" text={`${d.hidden_count} course-level items are hidden until verified, including estimated contact hours, employer lists with no official source and unsupported study-abroad claims. Each course page lists them.`} />
        </section>
      </div>
    </div>
  );
}
